// Global
import React, { JSX } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Content } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import classNames from 'classnames';
import { RichTextField, useSitecore } from '@sitecore-content-sdk/nextjs';

export type RTEProps = Content.Rte_Component & {
  isSidenavPart?: boolean;
  params: {
    rteField: string;
  };
};

const RTE = (props: RTEProps): JSX.Element => {
  const { text } = props?.fields || {};
  const { RenderingIdentifier, styles } = props?.params || {};
  const { page } = useSitecore();
  const pageTemplateId = page?.layout?.sitecore?.route?.templateId;

  const phKey = `hztl-rte-token-content-${props?.params?.DynamicPlaceholderId || 'default'}`;

  /*
   * RENDERING
   */

  const extendedTailwindVariants = tv({
    extend: TAILWIND_VARIANTS,
    slots: {
      base: [styles?.trimEnd()],
    },
  });

  const { base, contentContainer } = extendedTailwindVariants();
  const fields = page?.layout?.sitecore?.route?.fields;

  // Check if we should use page context field
  const showContentFromPageContext = !!(props.params?.rteField && fields?.[props.params.rteField]);

  // Get content from page context if available, otherwise use default datasource
  const pageContextContent = props.params?.rteField
    ? (fields?.[props.params.rteField] as RichTextField)
    : undefined;
  const richTextField = showContentFromPageContext ? pageContextContent : text;

  // Fallback to dictionary value if no content available
  const finalContent = !richTextField || richTextField.value === '' ? { ...text } : richTextField;

  // Check for any placeholder tokens
  const hasPlaceholderToken = finalContent?.value?.includes('[[ADDPLACEHOLDER]]');

  // Function to replace tokens with a single placeholder in the right position
  const replaceTokensWithPlaceholder = (content: string, className?: string) => {
    // Find the first token position
    const firstTokenMatch = content.match(/\[\[ADDPLACEHOLDER\]\]/);

    if (!firstTokenMatch) return content; // No tokens found

    const tokenIndex = firstTokenMatch.index || 0;
    const contentBefore = content.slice(0, tokenIndex);
    const contentAfter = content.slice(tokenIndex).replace(/\[\[ADDPLACEHOLDER\]\]/g, '');

    return (
      <>
        <RichTextWrapper
          field={{ value: contentBefore }}
          className={className}
          pageTemplateId={pageTemplateId}
        />
        <div className="[&_.inpage-banner-wrapper]:!px-0">
          <PlaceholderWrapper name={phKey} rendering={props.rendering} />
        </div>
        <RichTextWrapper
          field={{ value: contentAfter }}
          className={className}
          pageTemplateId={pageTemplateId}
        />
      </>
    );
  };

  const children = showContentFromPageContext ? (
    <div
      className={classNames(
        base(),
        !props?.isSidenavPart
          ? 'max-w-screen-xxl mx-auto px-10 lg:px-[80px] py-[48px]'
          : 'max-w-screen-xxl mx-auto pr-3 md:pr-6 lg:pr-12'
      )}
    >
      {hasPlaceholderToken ? (
        replaceTokensWithPlaceholder(
          pageContextContent?.value || '',
          '[&_p]:last:mb-8 [&_h1]:mt-16 lg:[&_h1]:mt-24 [&_h2]:mt-16 lg:[&_h2]:mt-24 [&_h3]:mt-8 lg:[&_h3]:mt-10 [&_h4]:mt-8 lg:[&_h4]:mt-10'
        )
      ) : (
        <RichTextWrapper
          field={pageContextContent}
          className="[&_p]:last:mb-8 [&_h1]:mt-16 lg:[&_h1]:mt-24 [&_h2]:mt-16 lg:[&_h2]:mt-24 [&_h3]:mt-8 lg:[&_h3]:mt-10 [&_h4]:mt-8 lg:[&_h4]:mt-10"
          pageTemplateId={pageTemplateId}
        />
      )}
    </div>
  ) : hasPlaceholderToken ? (
    replaceTokensWithPlaceholder(finalContent.value || '')
  ) : (
    <RichTextWrapper field={finalContent} pageTemplateId={pageTemplateId} />
  );

  if (finalContent && finalContent?.value === '') {
    return <></>;
  }

  return (
    <div
      className={classNames(
        base(),
        !props?.isSidenavPart
          ? 'px-10 lg:px-20 max-w-screen-xxl group-[]/modern:px-0 group-[]/modern:max-w-none mx-auto py-10 w-full'
          : ''
      )}
      data-component="authorable/shared/content/rte"
      id={RenderingIdentifier}
    >
      <div className={contentContainer()}>{children}</div>
    </div>
  );
};

export const Default = withStandardComponentWrapper(RTE);

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['component'],
    contentContainer: ['p-0', '[&_.ck-editor__editable]:!p-0'],
    placeholder: ['is-empty-hint'],
  },
});
